import { Point } from '../types';

/**
 * Converts an array of Points (with optional Bezier control handles cp1/cp2)
 * into a full SVG path string (D attribute) supporting Cubic Bezier curves (C).
 */
export function bezierPointsToSvgPath(points: Point[], closed: boolean = false): string {
  if (!points || points.length === 0) return '';
  if (points.length === 1) return `M ${points[0].x} ${points[0].y}`;

  let d = `M ${Math.round(points[0].x)} ${Math.round(points[0].y)}`;

  for (let i = 1; i < points.length; i++) {
    const prev = points[i - 1];
    const curr = points[i];

    if (prev.cp2 || curr.cp1) {
      const cp1x = prev.cp2 ? Math.round(prev.cp2.x) : Math.round(prev.x);
      const cp1y = prev.cp2 ? Math.round(prev.cp2.y) : Math.round(prev.y);
      const cp2x = curr.cp1 ? Math.round(curr.cp1.x) : Math.round(curr.x);
      const cp2y = curr.cp1 ? Math.round(curr.cp1.y) : Math.round(curr.y);
      const x = Math.round(curr.x);
      const y = Math.round(curr.y);
      d += ` C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${x} ${y}`;
    } else {
      d += ` L ${Math.round(curr.x)} ${Math.round(curr.y)}`;
    }
  }

  if (closed) {
    const last = points[points.length - 1];
    const first = points[0];
    if (last.cp2 || first.cp1) {
      const cp1x = last.cp2 ? Math.round(last.cp2.x) : Math.round(last.x);
      const cp1y = last.cp2 ? Math.round(last.cp2.y) : Math.round(last.y);
      const cp2x = first.cp1 ? Math.round(first.cp1.x) : Math.round(first.x);
      const cp2y = first.cp1 ? Math.round(first.cp1.y) : Math.round(first.y);
      const x = Math.round(first.x);
      const y = Math.round(first.y);
      d += ` C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${x} ${y} Z`;
    } else {
      d += ' Z';
    }
  }

  return d;
}

/**
 * Converts freehand pencil stroke points into a smooth SVG path
 * using distance filtering and quadratic Bezier smoothing.
 */
export function pencilPointsToSmoothSvgPath(points: Point[], closed: boolean = false): string {
  if (!points || points.length === 0) return '';
  if (points.length === 1) return `M ${Math.round(points[0].x)} ${Math.round(points[0].y)}`;
  if (points.length === 2) {
    return `M ${Math.round(points[0].x)} ${Math.round(points[0].y)} L ${Math.round(points[1].x)} ${Math.round(points[1].y)}${closed ? ' Z' : ''}`;
  }

  // Filter micro-jitter (points closer than 3px)
  const filtered: Point[] = [points[0]];
  for (let i = 1; i < points.length; i++) {
    const prev = filtered[filtered.length - 1];
    const curr = points[i];
    const dist = Math.hypot(curr.x - prev.x, curr.y - prev.y);
    if (dist >= 3 || i === points.length - 1) {
      filtered.push(curr);
    }
  }

  if (filtered.length <= 2) {
    return `M ${Math.round(filtered[0].x)} ${Math.round(filtered[0].y)} L ${Math.round(filtered[filtered.length - 1].x)} ${Math.round(filtered[filtered.length - 1].y)}${closed ? ' Z' : ''}`;
  }

  let d = `M ${Math.round(filtered[0].x)} ${Math.round(filtered[0].y)}`;
  for (let i = 1; i < filtered.length - 1; i++) {
    const xc = Math.round((filtered[i].x + filtered[i + 1].x) / 2);
    const yc = Math.round((filtered[i].y + filtered[i + 1].y) / 2);
    d += ` Q ${Math.round(filtered[i].x)} ${Math.round(filtered[i].y)}, ${xc} ${yc}`;
  }
  const last = filtered[filtered.length - 1];
  d += ` L ${Math.round(last.x)} ${Math.round(last.y)}`;
  if (closed) d += ' Z';
  return d;
}

/**
 * Unified SVG path generation function.
 */
export function pointsToSvgPath(points: Point[], closed: boolean = false, smooth: boolean = true): string {
  if (!points || points.length === 0) return '';
  
  // Check if any point contains Bezier control points (Pen Tool curve)
  const hasBezierControls = points.some(p => p.cp1 || p.cp2);
  if (hasBezierControls) {
    return bezierPointsToSvgPath(points, closed);
  }

  if (smooth) {
    return pencilPointsToSmoothSvgPath(points, closed);
  }

  // Linear path fallback
  let d = `M ${Math.round(points[0].x)} ${Math.round(points[0].y)}`;
  for (let i = 1; i < points.length; i++) {
    d += ` L ${Math.round(points[i].x)} ${Math.round(points[i].y)}`;
  }
  if (closed) d += ' Z';
  return d;
}

/**
 * Calculates a smooth tangent direction vector (dirX, dirY) for any anchor point,
 * ensuring handles NEVER collapse to length 0 even on endpoints or 2-point lines.
 */
export function getAnchorTangent(points: Point[], idx: number, closed: boolean = false, handleDist: number = 28): { dirX: number; dirY: number } {
  if (!points || points.length === 0) return { dirX: handleDist, dirY: 0 };
  const cur = points[idx];
  if (!cur) return { dirX: handleDist, dirY: 0 };

  let dx = 0;
  let dy = 0;

  if (closed && points.length > 2) {
    const prev = idx > 0 ? points[idx - 1] : points[points.length - 1];
    const next = idx < points.length - 1 ? points[idx + 1] : points[0];
    dx = next.x - prev.x;
    dy = next.y - prev.y;
  } else {
    if (idx === 0) {
      if (points.length > 1) {
        const next = points[1];
        dx = next.x - cur.x;
        dy = next.y - cur.y;
      } else {
        dx = handleDist;
        dy = 0;
      }
    } else if (idx === points.length - 1) {
      const prev = points[points.length - 2];
      dx = cur.x - prev.x;
      dy = cur.y - prev.y;
    } else {
      const prev = points[idx - 1];
      const next = points[idx + 1];
      dx = next.x - prev.x;
      dy = next.y - prev.y;
    }
  }

  const len = Math.hypot(dx, dy);
  if (len < 1e-4) {
    return { dirX: handleDist, dirY: 0 };
  }
  return {
    dirX: Math.round((dx / len) * handleDist),
    dirY: Math.round((dy / len) * handleDist)
  };
}
